// "@hotwired/stimulus" (v3), NOT the legacy "stimulus" package (v1) that is still
// in package.json. v1 has no `static values` API, so under it every *Value
// accessor is undefined, `enabledValue` reads false and the event is never sent.
// That import shipped on 2026-08-12 and recorded zero "form started" events in
// production for five weeks before anyone noticed.
import { Controller } from "@hotwired/stimulus"

// Fires one "form started" event the first time a visitor actually touches a
// form, and never again for that page view.
//
// Why this exists: a landing-page view and a submitted form are the only two
// things we could previously count, which cannot distinguish "nobody is
// interested" from "people try and give up". The gap between started and
// submitted is the abandonment rate, and it is the only number that says
// whether a funnel has a demand problem or a form problem. Those need opposite
// fixes, so guessing is expensive.
//
// Attach with:
//   data-controller="form-start"
//   data-form-start-event-value="B2B form started"
//   data-form-start-enabled-value="<%= Rails.env.production? %>"
export default class extends Controller {
  static values = {
    event:   String,
    enabled: Boolean,
    // Optional free-form context, e.g. the brand key.
    context: String
  }

  connect() {
    this.fired = false
    // `focusin` rather than per-field listeners: it bubbles, so fields rendered
    // later (the recipient rows are added dynamically) are covered without
    // rebinding. `change` catches selects and file inputs, which a user can
    // operate without ever firing focusin on some browsers.
    this.onTouch = this.handleTouch.bind(this)
    this.element.addEventListener("focusin", this.onTouch)
    this.element.addEventListener("change", this.onTouch)
  }

  disconnect() {
    // Turbolinks keeps the JS context alive across navigations, so a controller
    // that does not unbind leaks a listener per visit.
    this.element.removeEventListener("focusin", this.onTouch)
    this.element.removeEventListener("change", this.onTouch)
  }

  handleTouch() {
    if (this.fired) return
    this.fired = true

    // Unbind immediately. The event is once-per-page-view by definition and
    // there is no reason to keep handling input we will ignore.
    this.element.removeEventListener("focusin", this.onTouch)
    this.element.removeEventListener("change", this.onTouch)

    if (!this.enabledValue) return
    if (typeof window.ahoy === "undefined") return

    try {
      window.ahoy.track(this.eventValue, { context: this.contextValue || null })
    } catch (e) {
      // Analytics must never break the form it is measuring.
    }
  }
}
